import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Truck, Coins, UsersThree, ArrowRight, Lock, CheckCircle, Bank,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtCurrency } from "@/lib/format";
import { PRESETS, PRESET_BY_ID } from "@/lib/presets";
import HoverTip from "@/components/HoverTip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_INDUSTRY = "smallbiz";

const quickFromPreset = (preset) => ({
  num_vehicles: preset.inputs.num_vehicles,
  annual_contribution: preset.inputs.annual_contribution,
  num_key_employees: preset.inputs.num_key_employees,
});

export default function QuickBreakdown() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const urlIndustry = params.get("industry") || "";
  const initialIndustry = PRESET_BY_ID[urlIndustry] ? urlIndustry : DEFAULT_INDUSTRY;

  const [advisor, setAdvisor] = useState(null);
  const [industry, setIndustry] = useState(initialIndustry);
  const [inputs, setInputs] = useState(quickFromPreset(PRESET_BY_ID[initialIndustry]));
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/advisor/public`, { params: ref ? { ref } : {} })
      .then(({ data }) => setAdvisor(data))
      .catch(() => setAdvisor(null));
  }, [ref]);

  const changeIndustry = (id) => {
    setIndustry(id);
    setInputs(quickFromPreset(PRESET_BY_ID[id]));
  };

  const mergedInputs = () => ({ ...PRESET_BY_ID[industry].inputs, ...inputs });

  const calc = async () => {
    setCalculating(true);
    try {
      const { data } = await axios.post(`${API}/calculate`, mergedInputs());
      setResult(data);
      if (unlocked) {
        setShowResults(true);
      } else {
        setGateOpen(true); // numbers stay hidden until the lead form is submitted
      }
    } catch (e) {
      toast.error("Could not run breakdown");
    } finally {
      setCalculating(false);
    }
  };

  const handleLeadSubmitted = () => {
    setGateOpen(false);
    setUnlocked(true);
    setShowResults(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main data-testid="quickbreakdown-page" className="bg-brand-bone min-h-[calc(100vh-4rem)]">
      <Hero advisorName={advisor?.advisor_name} />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {showResults && result ? (
          <ResultsReveal
            result={result}
            advisor={advisor}
            industryLabel={PRESET_BY_ID[industry].label}
            onBack={() => setShowResults(false)}
          />
        ) : (
          <InputCard
            industry={industry}
            onIndustryChange={changeIndustry}
            inputs={inputs}
            setInputs={setInputs}
            calculating={calculating}
            onCalculate={calc}
          />
        )}
      </section>

      <LeadGateModal
        open={gateOpen}
        onOpenChange={setGateOpen}
        industry={industry}
        inputs={inputs}
        fullInputs={mergedInputs()}
        advisorName={advisor?.advisor_name}
        refCode={ref}
        onSubmitted={handleLeadSubmitted}
      />
    </main>
  );
}

function Hero({ advisorName }) {
  return (
    <section className="relative overflow-hidden border-b border-brand-line">
      <div
        className="absolute inset-0 opacity-[0.3]"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1708694557066-c9437fcfdc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZ3JlZW4lMjBhYnN0cmFjdCUyMHRleHR1cmV8ZW58MHx8fHwxNzc3MDYyMjU5fDA&ixlib=rb-4.1.0&q=85")',
          backgroundSize: "cover",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-bone via-brand-bone/95 to-brand-bone/40" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <span className="label-eyebrow">3-Minute Breakdown · Ontario CCPC</span>
        <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-forest leading-[1.05]">
          How much wealth is your business
          <br />
          <span className="italic text-brand-copper">leaking</span> every year?
        </h1>
        <p className="mt-6 text-lg text-brand-ink/75 max-w-2xl">
          Pick your industry, confirm three numbers, and get your exact leakage amount —
          plus a match with {advisorName || "a licensed advisor"} to fix it.
        </p>
      </div>
    </section>
  );
}

function InputCard({ industry, onIndustryChange, inputs, setInputs, calculating, onCalculate }) {
  return (
    <Card>
      <span className="label-eyebrow">Step 1 of 2 · Tell us about your business</span>
      <h2 className="mt-3 font-serif text-3xl text-brand-forest">Your industry &amp; three numbers</h2>

      <div className="mt-7">
        <HoverTip id="qb-industry" label="Your industry" />
        <Select value={industry} onValueChange={onIndustryChange}>
          <SelectTrigger data-testid="qb-industry-select" className="mt-2 bg-white border-brand-line h-12 rounded-md text-base">
            <SelectValue placeholder="Select your industry…" />
          </SelectTrigger>
          <SelectContent className="bg-white border-brand-line max-h-72">
            {PRESETS.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`qb-industry-${p.id}`}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1.5 text-xs text-brand-muted">
          We pre-fill industry-average assumptions — your three numbers below override them.
        </p>
      </div>

      <div className="mt-8 space-y-6">
        <QuickField
          icon={Truck}
          label="Financed vehicles or equipment"
          hint="Trucks, trailers, excavators, anything you make payments on."
          suffix="UNITS"
          value={inputs.num_vehicles}
          onChange={(v) => setInputs((s) => ({ ...s, num_vehicles: parseInt(v) || 0 }))}
          testId="qb-num-vehicles"
        />
        <QuickField
          icon={Coins}
          label="Annual retained earnings (rough)"
          hint="What's left in the company after taxes each year — the money sitting passively."
          prefix="$"
          value={inputs.annual_contribution}
          onChange={(v) => setInputs((s) => ({ ...s, annual_contribution: parseFloat(v) || 0 }))}
          testId="qb-annual-contribution"
        />
        <QuickField
          icon={UsersThree}
          label="Key employees you can't afford to lose"
          hint="Foremen, estimators, project managers, lead techs."
          suffix="PEOPLE"
          value={inputs.num_key_employees}
          onChange={(v) => setInputs((s) => ({ ...s, num_key_employees: parseInt(v) || 0 }))}
          testId="qb-num-key-employees"
        />
      </div>

      <div className="mt-10">
        <Button
          onClick={onCalculate}
          disabled={calculating}
          data-testid="qb-btn-calculate"
          className="bg-brand-forest hover:bg-brand-forestHover text-brand-bone rounded-md h-12 px-6 gap-2 w-full sm:w-auto"
        >
          {calculating ? "Crunching numbers…" : "Calculate my leakage"}
          <ArrowRight size={16} weight="bold" />
        </Button>
      </div>
    </Card>
  );
}

const EMPTY_CONTACT = { full_name: "", business_name: "", email: "", phone: "" };

function LeadGateModal({ open, onOpenChange, industry, inputs, fullInputs, advisorName, refCode, onSubmitted }) {
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!contact.full_name.trim() || !contact.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...contact,
        ...inputs,
        industry,
        full_inputs: fullInputs,
        ref: refCode,
      });
      toast.success("You're in — revealing your numbers");
      onSubmitted();
    } catch (e) {
      toast.error("Submission failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="qb-lead-modal" className="bg-white border-brand-line max-w-lg">
        <DialogHeader>
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-brand-cream text-brand-forest">
            <Lock weight="duotone" size={22} />
          </span>
          <DialogTitle className="font-serif text-2xl md:text-3xl text-brand-forest leading-tight pt-2">
            Your results are ready.
          </DialogTitle>
          <DialogDescription className="text-sm text-brand-ink/70 leading-relaxed">
            Based on your industry, you could be saving <strong className="text-brand-forest">thousands
            over the next 5–10 years</strong>. Enter your name, company, email, and phone number to see
            your exact leakage amount — and get matched with {advisorName ? <strong className="text-brand-forest">{advisorName}</strong> : "an Advisor"} to fix it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <CapField label="Full name *" id="lead_name" value={contact.full_name} onChange={(v) => setContact((s) => ({ ...s, full_name: v }))} testId="qb-lead-name" placeholder="Mark Armstrong" />
          <CapField label="Company" id="lead_company" value={contact.business_name} onChange={(v) => setContact((s) => ({ ...s, business_name: v }))} testId="qb-lead-company" placeholder="Bay Roofing Ltd." />
          <CapField label="Email *" id="lead_email" type="email" value={contact.email} onChange={(v) => setContact((s) => ({ ...s, email: v }))} testId="qb-lead-email" placeholder="mark@bayroofing.ca" />
          <CapField label="Phone" id="lead_phone" value={contact.phone} onChange={(v) => setContact((s) => ({ ...s, phone: v }))} testId="qb-lead-phone" placeholder="(705) 555-0188" />
        </div>

        <Button
          onClick={submit}
          disabled={submitting}
          data-testid="qb-lead-submit"
          className="mt-2 bg-brand-copper hover:bg-brand-copperHover text-white rounded-md h-12 gap-2 w-full"
        >
          {submitting ? "Unlocking…" : "Show my exact leakage"}
          <ArrowRight size={16} weight="bold" />
        </Button>
        <p className="text-xs text-brand-muted text-center">
          We don't share your information. Hypothetical illustration only — not a
          guarantee of any specific result.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function ResultsReveal({ result, advisor, industryLabel, onBack }) {
  const minis = [
    ["Tax drag saved", result.headline_tax_drag_saved],
    ["Interest recaptured", result.headline_interest_recaptured],
    ["Retention value", result.headline_retention_value],
    ["IBC cash value", result.headline_ibc_cash_value],
  ];
  return (
    <div data-testid="qb-results">
      <div
        data-testid="qb-success-banner"
        className="mb-6 flex items-center gap-3 bg-brand-forest/5 border border-brand-forest/20 rounded-md px-5 py-4"
      >
        <CheckCircle size={22} weight="fill" className="text-brand-forest shrink-0" />
        <div className="text-sm text-brand-ink/85">
          <strong className="text-brand-forest">An Advisor will be in touch with you shortly.</strong>{" "}
          {advisor?.advisor_name ? `${advisor.advisor_name} has received your details` : "Your details have been received"} —
          expect a call or email within one business day. A copy of your numbers is on its way to your inbox.
        </div>
      </div>

      <div className="bg-brand-forest text-brand-bone rounded-md p-8 md:p-10 mb-6 grain relative overflow-hidden" data-testid="qb-teaser">
        <div className="relative">
          <div className="flex items-center gap-2 text-brand-bone/60 text-xs uppercase tracking-[0.2em] font-semibold">
            <Bank size={14} weight="duotone" /> Your 10-year projection · {industryLabel}
          </div>
          <div className="mt-5 font-serif text-5xl md:text-6xl text-brand-bone leading-[1.05] break-words">
            {fmtCurrency(result.headline_wealth_gain)}
          </div>
          <p className="mt-4 text-brand-bone/75 text-base md:text-lg max-w-xl">
            Estimated wealth gain by switching from "borrow from the bank" to
            "build a bank" — based on your numbers and industry-average assumptions.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-8 max-w-md">
            <SubTile label="5-yr gain" value={fmtCurrency(result.summary_5yr.total_wealth_gain)} />
            <SubTile label="10-yr gain" value={fmtCurrency(result.summary_10yr.total_wealth_gain)} accent />
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {minis.map(([label, value]) => (
          <div key={label} className="bg-white border border-brand-line rounded-md p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">{label}</div>
            <div className="mt-1.5 font-serif text-2xl text-brand-forest">{fmtCurrency(value)}</div>
          </div>
        ))}
      </div>

      <Card>
        <div className="text-center py-2">
          <h3 className="font-serif text-2xl text-brand-forest">Where this leakage comes from</h3>
          <p className="mt-3 text-sm text-brand-ink/70 max-w-lg mx-auto">
            Passive-income tax at 50.17%, financing interest paid to outside lenders, and the
            cost of losing key people. Your advisor will walk you through the full year-by-year
            breakdown and what a properly structured plan looks like for your business.
          </p>
          {advisor?.email && (
            <p className="mt-5 text-sm text-brand-muted">
              Want it sooner? Email <span className="text-brand-forest font-semibold">{advisor.email}</span>
              {advisor.phone && <> or call <span className="text-brand-forest font-semibold">{advisor.phone}</span></>}.
            </p>
          )}
          <Button
            variant="ghost"
            onClick={onBack}
            data-testid="qb-btn-back"
            className="mt-5 text-brand-muted hover:text-brand-forest rounded-md"
          >
            ← Adjust my numbers and run it again
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white border border-brand-line rounded-md p-6 md:p-10 shadow-sm">
      {children}
    </div>
  );
}

function QuickField({ icon: Icon, label, hint, prefix, suffix, value, onChange, testId }) {
  return (
    <div className="flex gap-4 items-start">
      <span className="mt-7 inline-flex items-center justify-center w-11 h-11 rounded-md bg-brand-cream text-brand-forest shrink-0">
        <Icon size={22} weight="duotone" />
      </span>
      <div className="flex-1">
        <HoverTip id={testId} label={label} />
        <div className="relative mt-2">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm font-medium pointer-events-none">{prefix}</span>
          )}
          <Input
            data-testid={testId}
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`bg-white border-brand-line focus-visible:ring-2 focus-visible:ring-brand-forest rounded-md h-12 text-base ${prefix ? "pl-7" : ""} ${suffix ? "pr-12" : ""}`}
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted text-[10px] font-semibold uppercase tracking-wider pointer-events-none">{suffix}</span>
          )}
        </div>
        <p className="mt-1.5 text-xs text-brand-muted">{hint}</p>
      </div>
    </div>
  );
}

function CapField({ label, id, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <HoverTip id={testId} label={label} htmlFor={id} />
      <Input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 bg-white border-brand-line focus-visible:ring-2 focus-visible:ring-brand-forest rounded-md h-11"
      />
    </div>
  );
}

function SubTile({ label, value, accent }) {
  return (
    <div className={`rounded-md p-4 border ${accent ? "bg-brand-copper/20 border-brand-copper/40" : "bg-brand-bone/10 border-brand-bone/20"}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-brand-bone/60">{label}</div>
      <div className={`mt-1.5 font-serif text-2xl ${accent ? "text-brand-copper" : "text-brand-bone"}`}>
        {value}
      </div>
    </div>
  );
}
